import { Anchor, Box, Group, Text } from "@mantine/core";
import { privacyPolicyUrl, termsOfServiceUrl } from "./v2/legalLinks";

// ACA-33/35: single source of truth for the onboarding "chrome" shared across
// screens — the step labels, the footer (links + copyright), and the logo.
// Import these instead of redefining them per screen.

export const REGISTRATION_STEPS = [
  { label: "Create an account" },
  { label: "Tell us about yourself" },
  { label: "Get started" },
];

export const ONBOARDING_LOGO_SRC = "/assets/img/logos/header-mark-left.svg";

export interface OnboardingHeaderProps {
  signedInAs?: string;
  logoTestId?: string;
  signedInTestId?: string;
}

// Shared top bar: logo (left) · signed-in (right). Used by the card stage
// (Intent + Get started) and the profile screen, which differ only in their
// test ids.
export function OnboardingHeader({
  signedInAs,
  logoTestId,
  signedInTestId,
}: OnboardingHeaderProps) {
  return (
    <Box
      component="header"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
        padding: "1.25rem 2rem",
      }}
    >
      <img src={ONBOARDING_LOGO_SRC} alt="ArtCloud" data-testid={logoTestId} style={{ height: 40 }} />
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontSize: 12,
        }}
      >
        {signedInAs ? (
          <Text span size="xs" c="gray.6" data-testid={signedInTestId} inherit>
            Signed in as {signedInAs}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
}

// Shared footer used by every onboarding screen (card and two-column alike).
export function OnboardingFooter() {
  const footerLinks = [
    { label: "Help", href: "https://help.artcloud.com" },
    { label: "Privacy", href: privacyPolicyUrl() },
    { label: "Terms", href: termsOfServiceUrl() },
  ];

  return (
    <Box
      component="footer"
      data-testid="screen-footer"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: "1.5rem",
        padding: "1.5rem",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
      }}
    >
      <Text size="xs" c="gray.5" inherit>
        © 2026 Artlogic Ltd · ArtCloud for artists
      </Text>
      <Group gap="lg">
        {footerLinks.map((link) => (
          <Anchor
            key={link.label}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            size="xs"
            c="gray.6"
            fw={500}
            inherit
          >
            {link.label}
          </Anchor>
        ))}
      </Group>
    </Box>
  );
}
